import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  analyzeTenantSchemaBaseline,
  compileTenantSchemaBaseline,
  getLatestTenantSchemaBaseline,
  getTenantSchemaOverview,
  getTenantSchemaPlans,
  registerTenantSchemaArtifacts,
} from '../api/tenantSchemaApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { DetailField } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
  gap: 2.5,
  minWidth: 0,
  maxWidth: '100%',
  '& > *': { minWidth: 0, maxWidth: '100%' },
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const mutedSx = {
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.secondary,
};

function resolveSchemaError(err, t) {
  const payload = err?.response?.data || {};
  const rawKey = payload.message_key;
  if (rawKey) {
    const i18nKey = String(rawKey).startsWith('tenantSchema.')
      ? `tenantSchema:${String(rawKey).slice('tenantSchema.'.length)}`
      : rawKey;
    return t(i18nKey, { defaultValue: payload.message || t('tenantSchema:dashboard.load_failed') });
  }
  return payload.message || t('tenantSchema:dashboard.load_failed');
}

export function TenantSchemaDashboardPage() {
  const { t } = useTranslation(['tenantSchema', 'common']);
  const navigate = useNavigate();
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.tenant_schema.manage');

  const [overview, setOverview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);

  const emptyValue = t('common:empty_value');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewResponse, plansResponse, baselineResponse] = await Promise.all([
        getTenantSchemaOverview(),
        getTenantSchemaPlans({ per_page: 20 }),
        getLatestTenantSchemaBaseline().catch(() => null),
      ]);
      setOverview(overviewResponse.data || overviewResponse);
      setPlans(plansResponse.data || []);
      setBaseline(baselineResponse?.data || overviewResponse?.data?.baseline_plan || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        id: 'tenant_id',
        label: t('tenantSchema:dashboard.columns.tenant'),
        render: (row) => (
          row.tenant_name
          || t('tenantSchema:dashboard.tenant_link', { id: row.tenant_id })
          || emptyValue
        ),
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? <StatusBadge status={row.status} category="schema_plan" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'mode',
        label: t('tenantSchema:dashboard.columns.mode'),
        render: (row) => (
          row.mode
            ? (
              <StatusBadge
                status={row.mode}
                category="generic"
                apiLabel={row.mode}
                size="Sm"
                tone="Info"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'included_migrations_count',
        label: t('tenantSchema:dashboard.columns.included'),
        render: (row) => (
          row.included_migrations_count === 0 || row.included_migrations_count
            ? row.included_migrations_count
            : emptyValue
        ),
      },
      {
        id: 'updated_at',
        label: t('tenantSchema:dashboard.columns.updated'),
        render: (row) => {
          if (!row.updated_at) return emptyValue;
          const date = new Date(row.updated_at);
          return Number.isNaN(date.getTime()) ? row.updated_at : date.toLocaleString();
        },
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              if (row.tenant_id) {
                navigate(`/tenants/${row.tenant_id}`);
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  const runPreview = async () => {
    setBusy(true);
    setActionError('');
    setActionMessage('');
    try {
      const res = await registerTenantSchemaArtifacts({ version: 'v1', dry_run: true });
      setPreview(res.data || res);
      setActionMessage(t('tenantSchema:messages.artifactRegistrationPreview'));
    } catch (err) {
      setActionError(resolveSchemaError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const runRegister = async () => {
    setBusy(true);
    setActionError('');
    setActionMessage('');
    try {
      const res = await registerTenantSchemaArtifacts({
        version: 'v1',
        dry_run: false,
        confirmation: confirmation.trim(),
      });
      const data = res.data || res;
      setPreview(data);
      setActionMessage(t('tenantSchema:messages.artifactsRegistered'));
      setConfirmation('');
      await load();
    } catch (err) {
      setActionError(resolveSchemaError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const runAnalyzeBaseline = async () => {
    setBusy(true);
    setActionError('');
    setActionMessage('');
    try {
      const res = await analyzeTenantSchemaBaseline({ version: 'v1' });
      const data = res.data || res;
      setAnalysis(data);
      setActionMessage(t('tenantSchema:baseline.analyzeComplete'));
    } catch (err) {
      setActionError(resolveSchemaError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const runCompileBaseline = async () => {
    setBusy(true);
    setActionError('');
    setActionMessage('');
    try {
      const res = await compileTenantSchemaBaseline({ version: 'v1' });
      const data = res.data || res;
      setAnalysis(data.analysis || data);
      setBaseline(data.baseline_plan || null);
      setActionMessage(t('tenantSchema:baseline.draftCreated'));
      await load();
    } catch (err) {
      setActionError(resolveSchemaError(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (error && !overview) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <PageHeader
          type="List"
          title={t('tenantSchema:dashboard.title')}
          subtitle={t('tenantSchema:dashboard.subtitle')}
        />
        <SectionCard variant="Danger">
          <Alert
            severity="error"
            sx={{ bgcolor: 'transparent', p: 0 }}
            action={(
              <Button variant="Ghost" onClick={load} sx={{ minHeight: 32 }}>
                {t('common:retry')}
              </Button>
            )}
          >
            {t('tenantSchema:dashboard.load_failed')}
          </Alert>
        </SectionCard>
      </Box>
    );
  }

  const data = overview || {};
  const latest = data.latest_version;
  const confirmationOk = confirmation.trim().toLowerCase() === 'register-v1';
  const counts = preview?.counts;
  const baselineCounts = analysis?.counts || baseline?.counts;
  const report = analysis?.report || baseline?.report;
  const moduleScope = analysis?.module_scope || baseline?.module_scope || [];
  const recordCount = plans.length;
  const hasArtifacts = Boolean(latest?.artifacts_count > 0);

  const overviewMetrics = [
    { key: 'versions', label: t('tenantSchema:dashboard.metrics.versions'), value: data.total_versions ?? 0 },
    { key: 'approved', label: t('tenantSchema:dashboard.metrics.approved'), value: data.approved_versions ?? 0 },
    { key: 'plans', label: t('tenantSchema:dashboard.metrics.plans'), value: data.total_plans ?? 0 },
    { key: 'blocked', label: t('tenantSchema:dashboard.metrics.blocked'), value: data.blocked_plans ?? 0 },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('tenantSchema:dashboard.title')}
        subtitle={t('tenantSchema:dashboard.subtitle')}
      />

      {loading ? (
        <LoadingSkeleton type="Detail" />
      ) : (
        <Stack spacing={2.5}>
          {!data.migration_runner_enabled && (
            <Alert severity="warning">
              {t('tenantSchema:warnings.runner_disabled')}
            </Alert>
          )}

          <Alert severity="info">
            {t('tenantSchema:messages.artifactRegistrationDoesNotApplyMigrations')}
          </Alert>
          <Alert severity="warning">
            {t('tenantSchema:baseline.rawArtifactsAreNotAppliedDirectly')}
          </Alert>

          {actionMessage && <Alert severity="success">{actionMessage}</Alert>}
          {actionError && <Alert severity="error">{actionError}</Alert>}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
              width: '100%',
            }}
          >
            {overviewMetrics.map((card) => (
              <MetricCard key={card.key} value={card.value} label={card.label} />
            ))}
          </Box>

          {latest && (
            <SectionCard variant="Default" title={t('tenantSchema:labels.version')} sx={{ mb: 0 }}>
              <Box sx={detailGridSx}>
                <DetailField label={t('tenantSchema:labels.version')}>
                  <Typography sx={monoSx}>{latest.version || emptyValue}</Typography>
                </DetailField>
                <DetailField
                  label={t('tenantSchema:versions.columns.migrations')}
                  value={latest.migration_count ?? 0}
                />
                <DetailField
                  label={t('tenantSchema:versions.columns.artifacts')}
                  value={latest.artifacts_count ?? 0}
                />
                {latest.last_registered_at && (
                  <DetailField
                    label={t('tenantSchema:labels.lastRegisteredAt')}
                    value={latest.last_registered_at}
                  />
                )}
              </Box>
            </SectionCard>
          )}

          {canManage && (
            <SectionCard variant="Default" title={t('tenantSchema:actions.registerArtifacts')} sx={{ mb: 0 }}>
              <Typography sx={{ ...mutedSx, mb: 2 }}>
                {t('tenantSchema:messages.artifactRegistrationDoesNotApplyMigrations')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Button variant="Secondary" disabled={busy} onClick={runPreview}>
                  {t('tenantSchema:actions.previewArtifactRegistration')}
                </Button>
                <Button
                  variant="Accent"
                  disabled={busy || !confirmationOk}
                  onClick={runRegister}
                >
                  {t('tenantSchema:actions.registerArtifacts')}
                </Button>
              </Stack>
              <Box sx={{ maxWidth: 360 }}>
                <TextInput
                  density="Compact"
                  label={t('tenantSchema:actions.confirmRegisterArtifacts')}
                  helperText="register-v1"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                />
              </Box>
              {counts && (
                <Box sx={{ ...detailGridSx, mt: 2.5 }}>
                  <DetailField label={t('tenantSchema:labels.filesScanned')} value={counts.files_scanned ?? 0} />
                  <DetailField label={t('tenantSchema:labels.created')} value={counts.created ?? 0} />
                  <DetailField label={t('tenantSchema:labels.updated')} value={counts.updated ?? 0} />
                  <DetailField label={t('tenantSchema:labels.unchanged')} value={counts.unchanged ?? 0} />
                  <DetailField label={t('tenantSchema:labels.changed')} value={counts.changed ?? 0} />
                </Box>
              )}
            </SectionCard>
          )}

          {canManage && (
            <SectionCard variant="Default" title={t('tenantSchema:baseline.title')} sx={{ mb: 0 }}>
              <Typography sx={{ ...mutedSx, mb: 1 }}>
                {t('tenantSchema:baseline.doesNotApplyMigrations')}
              </Typography>
              <Typography sx={{ ...mutedSx, mb: 2 }}>
                {t('tenantSchema:baseline.rawArtifactsAreNotAppliedDirectly')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Button
                  variant="Secondary"
                  disabled={busy || !hasArtifacts}
                  onClick={runAnalyzeBaseline}
                >
                  {t('tenantSchema:baseline.analyze')}
                </Button>
                <Button
                  variant="Accent"
                  disabled={busy || !hasArtifacts}
                  onClick={runCompileBaseline}
                >
                  {t('tenantSchema:baseline.createDraft')}
                </Button>
              </Stack>

              {(baseline || analysis) && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
                  <StatusBadge
                    status={baseline?.status || analysis?.status || 'review'}
                    category="schema_plan"
                    apiLabel={`${t('tenantSchema:baseline.reviewRequired')}: ${baseline?.status || analysis?.status || 'n/a'}`}
                    size="Sm"
                    tone={baseline?.status === 'approved' ? 'Success' : 'Warning'}
                  />
                  {moduleScope.length > 0 && (
                    <Typography sx={{ ...monoSx, color: semantic.text.secondary }}>
                      {t('tenantSchema:baseline.moduleScope')}: {moduleScope.join(', ')}
                    </Typography>
                  )}
                </Stack>
              )}

              {baselineCounts && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr 1fr',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      md: 'repeat(6, minmax(0, 1fr))',
                    },
                    gap: 2,
                    width: '100%',
                    mb: 2,
                  }}
                >
                  <MetricCard label={t('tenantSchema:baseline.total')} value={baselineCounts.total_artifacts ?? 0} />
                  <MetricCard
                    label={t('tenantSchema:baseline.included')}
                    value={(baselineCounts.include_core_global ?? 0) + (baselineCounts.include_selected_modules ?? 0)}
                  />
                  <MetricCard
                    label={t('tenantSchema:baseline.excluded')}
                    value={(baselineCounts.exclude_system_infra ?? 0) + (baselineCounts.exclude_inactive_modules ?? 0)}
                  />
                  <MetricCard label={t('tenantSchema:baseline.manualReview')} value={baselineCounts.manual_review_required ?? 0} />
                  <MetricCard label={t('tenantSchema:baseline.destructiveBlocked')} value={baselineCounts.destructive_blocked ?? 0} />
                  <MetricCard label={t('tenantSchema:baseline.unknown')} value={baselineCounts.unknown ?? 0} />
                </Box>
              )}

              {report && (
                <Stack spacing={1}>
                  <Typography sx={{ fontSize: 13, fontWeight: typographyTokens.fontWeightSemibold, color: semantic.text.primary }}>
                    {t('tenantSchema:baseline.dependencyWarnings')}: {report.dependency?.warnings?.length ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: typographyTokens.fontWeightSemibold, color: semantic.text.primary }}>
                    {t('tenantSchema:baseline.consolidationSuggestions')}: {report.consolidation_suggestions?.length ?? 0}
                  </Typography>
                  {(report.destructive_blocked || []).slice(0, 5).map((name) => (
                    <Typography key={name} sx={{ ...monoSx, color: semantic.error }}>{name}</Typography>
                  ))}
                  {(report.manual_review || []).slice(0, 5).map((name) => (
                    <Typography key={name} sx={{ ...monoSx, color: semantic.warning }}>{name}</Typography>
                  ))}
                  {(report.consolidation_suggestions || []).slice(0, 5).map((group) => (
                    <Typography key={group.table_name} sx={monoSx}>
                      {group.table_name}: {group.proposed_action} ({(group.source_migrations || []).length})
                    </Typography>
                  ))}
                </Stack>
              )}
            </SectionCard>
          )}

          <SectionCard variant="Default" noPadding sx={{ width: '100%', mb: 0 }}>
            <TableToolbar
              mode="Default"
              title={t('tenantSchema:dashboard.recent_plans')}
              meta={(
                <StatusBadge
                  status="list"
                  category="generic"
                  apiLabel={t('tenantSchema:dashboard.toolbar_count', { count: recordCount })}
                  size="Sm"
                  tone="Neutral"
                />
              )}
            />
            <DataTable
              type="Default"
              embedded
              columns={columns}
              rows={plans}
              getRowKey={(row) => row.id}
              emptyMessage={t('tenantSchema:dashboard.no_plans')}
              sx={{
                overflowX: 'auto',
                width: '100%',
                '& table': {
                  width: '100%',
                  minWidth: '100%',
                },
              }}
            />
          </SectionCard>
        </Stack>
      )}
    </Box>
  );
}
